
import React from "react";
import { Collapsible, CollapsibleTrigger, CollapsibleContent } from "@/components/ui/collapsible";
import { Table, TableHeader, TableBody, TableHead, TableRow, TableCell } from "@/components/ui/table";
import { ScrollArea } from "@/components/ui/scroll-area";
import { ChevronDown } from "lucide-react";
import { LeaderboardRecord } from "./types";
import { formatNumberWithCommas } from "./utils";

interface DataTableProps {
  isTableOpen: boolean;
  setIsTableOpen: (open: boolean) => void;
  filteredData: LeaderboardRecord[];
}

export const DataTable: React.FC<DataTableProps> = ({
  isTableOpen,
  setIsTableOpen,
  filteredData
}) => {
  return (
    <div className="bg-secondary/80 p-4 rounded-lg border border-primary/20">
      <Collapsible open={isTableOpen} onOpenChange={setIsTableOpen} className="w-full">
        <CollapsibleTrigger className="w-full">
          <div className="flex items-center justify-between w-full cursor-pointer">
            <h3 className="text-primary-light font-medium">Revenue and Forecast Detailed Data</h3>
            <div className="flex items-center">
              <span className="text-xs text-gray-400 mr-2">(Click to expand or collapse)</span>
              <ChevronDown className={`h-5 w-5 text-primary transition-transform ${isTableOpen ? 'transform rotate-180' : ''}`} />
            </div>
          </div>
        </CollapsibleTrigger>
        <CollapsibleContent className="mt-2">
          <ScrollArea className="h-[400px] w-full rounded-md border border-primary/20">
            <Table>
              <TableHeader className="bg-secondary-dark sticky top-0">
                <TableRow>
                  <TableHead className="text-[10px] text-primary font-medium w-12">SN</TableHead>
                  <TableHead className="text-[10px] text-primary font-medium w-12">ID</TableHead>
                  <TableHead className="text-[10px] text-primary font-medium">Account Name</TableHead>
                  <TableHead className="text-[10px] text-primary font-medium">Name</TableHead>
                  <TableHead className="text-[10px] text-primary font-medium">Region</TableHead>
                  <TableHead className="text-[10px] text-primary font-medium">Country</TableHead>
                  <TableHead className="text-[10px] text-primary font-medium">Product Line</TableHead>
                  <TableHead className="text-[10px] text-primary font-medium">Industry</TableHead>
                  <TableHead className="text-[10px] text-primary font-medium">Target</TableHead>
                  <TableHead className="text-[10px] text-primary font-medium">Forecast</TableHead>
                  <TableHead className="text-[10px] text-primary font-medium">Achievement</TableHead>
                  <TableHead className="text-[10px] text-primary font-medium">Y-1 Forecast</TableHead>
                  <TableHead className="text-[10px] text-primary font-medium">Y-1 Actuals</TableHead>
                  <TableHead className="text-[10px] text-primary font-medium">Y-2 Forecast</TableHead>
                  <TableHead className="text-[10px] text-primary font-medium">Y-2 Actuals</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {filteredData.map((record, index) => (
                  <TableRow key={record.internal_id} className="hover:bg-secondary/50">
                    <TableCell className="text-gray-300 font-normal text-[10px]">{index + 1}</TableCell>
                    <TableCell className="text-gray-300 font-normal text-[10px]">{record.id}</TableCell>
                    <TableCell className="text-gray-300 font-normal text-[10px]">{record.account_name || "N/A"}</TableCell>
                    <TableCell className="text-gray-300 font-normal text-[10px]">{record.name}</TableCell>
                    <TableCell className="text-gray-300 font-normal text-[10px]">{record.region}</TableCell>
                    <TableCell className="text-gray-300 font-normal text-[10px]">{record.country || "N/A"}</TableCell>
                    <TableCell className="text-gray-300 font-normal text-[10px]">{record.product_line}</TableCell>
                    <TableCell className="text-gray-300 font-normal text-[10px]">{record.industry}</TableCell>
                    <TableCell className="text-gray-300 font-normal text-[10px]">{formatNumberWithCommas(record.target)}</TableCell>
                    <TableCell className="text-gray-300 font-normal text-[10px]">{formatNumberWithCommas(record.forecast)}</TableCell>
                    <TableCell className="text-gray-300 font-normal text-[10px]">{formatNumberWithCommas(record.achievement)}</TableCell>
                    <TableCell className="text-gray-300 font-normal text-[10px]">{formatNumberWithCommas(record.y_1_forecast)}</TableCell>
                    <TableCell className="text-gray-300 font-normal text-[10px]">{formatNumberWithCommas(record.y_1_actuals)}</TableCell>
                    <TableCell className="text-gray-300 font-normal text-[10px]">{formatNumberWithCommas(record.y_2_forecast)}</TableCell>
                    <TableCell className="text-gray-300 font-normal text-[10px]">{formatNumberWithCommas(record.y_2_actuals)}</TableCell>
                  </TableRow>
                ))}
                {filteredData.length === 0 && (
                  <TableRow>
                    <TableCell colSpan={15} className="text-center text-gray-400 py-4">
                      No forecast data available
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </ScrollArea>
        </CollapsibleContent>
      </Collapsible>
    </div>
  );
};
